import { defineStore } from 'pinia'
import type { AuthState, LoginCredentials, LoginResponse } from '@/types/auth'
import { authApi } from '@/api/auth'

const SWITCHABLE_ROLES = ['customer', 'salesman', 'main_reseller', 'sub_reseller']

// STAB-P1-01 S2: refresh-storm 防護（module-level, 非 reactive）。
// 當 N 個並發請求同時收到 401 → 共用同一個 refresh Promise,不要 fan-out
// 打到後端觸發 rate-limit / REUSE-detect family-revoke。
let refreshInFlight: Promise<string> | null = null

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    accessToken: null,
    // STAB-P1-01 S2: refresh token 由後端 ROTATE；前端必須保留最新一份。
    refreshToken: null,
    activeIdentity: null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.accessToken && !!state.user,

    // 當前展示的身分（activeIdentity 優先，fallback 到 primary role）
    currentIdentity: (state): string | null =>
      state.activeIdentity ?? state.user?.role ?? null,

    // 用戶擁有的「可切換身分」清單（排除 admin）
    switchableIdentities: (state): string[] => {
      if (!state.user?.roles) return []
      return state.user.roles.filter(r => SWITCHABLE_ROLES.includes(r))
    },

    // 是否有多個可切換身分
    hasMultipleIdentities(): boolean {
      return this.switchableIdentities.length > 1
    },

    // 各身分檢查（用 currentIdentity）
    isCustomer(): boolean {
      return this.currentIdentity === 'customer'
    },
    isSalesman(): boolean {
      return this.currentIdentity === 'salesman'
    },
    isReseller(): boolean {
      return ['main_reseller', 'sub_reseller'].includes(this.currentIdentity ?? '')
    },
    // admin 判斷用 primary role（不受 activeIdentity 影響）
    isAdmin: (state) =>
      state.user?.role === 'site_admin' || state.user?.role === 'site_owner',

    // 向後相容
    hasRole: (state) => (role: string) => state.user?.roles?.includes(role) ?? false,
    userRoles: (state) => state.user?.roles ?? [],
    isSiteAdmin: (state) => state.user?.role === 'site_admin',
    isSiteOwner: (state) => state.user?.role === 'site_owner',
  },

  actions: {
    async login(credentials: LoginCredentials) {
      const response = await authApi.login(credentials)
      this.setAuthData(response)
      return response
    },

    async logout() {
      try {
        // 後端自 Unit A 起會 server-side revoke session（idempotent）。
        // 把 refresh token 一起帶過去可確保 client-side refresh 也被撤銷
        // （即使 access JWT 已過期、bearer 取不到 sid，refresh token 仍可
        // 由 LogoutRequest.refresh_token 欄位送到 server）。
        await authApi.logout(this.refreshToken ?? undefined)
      } catch {
        // Ignore logout errors
      } finally {
        this.clearAuthData()
      }
    },

    // STAB-P1-01 S2: refresh action.
    //
    // 設計原則：
    //   1. **refresh-storm 防護**：若已有一個 refresh 正在進行，所有
    //      並發呼叫者必須共用同一個 Promise（不要 fan-out）。
    //   2. **失敗 = forced re-login**：拋出錯誤，讓 401 interceptor 走清空
    //      + 跳 login + toast 流程。**不靜默成功**。
    //   3. **同步旋轉**：成功後必須 atomically 更新 accessToken + refreshToken
    //      + localStorage（後端 ROTATE，舊 refresh token 立即失效）。
    //   4. **保留 ADR-014 身分切換語意**：refresh 成功後 activeIdentity 不變
    //      （refresh 不重新登入，身分沿用）。
    //
    // Returns the new access token on success.
    async refresh(): Promise<string> {
      // Storm guard: if a refresh is already in flight, return the same Promise.
      // This is the only piece of state NOT in AuthState (it's transient and
      // intentionally non-reactive — concurrent callers share the same Promise).
      const inflight = refreshInFlight
      if (inflight) {
        return inflight
      }
      if (!this.refreshToken) {
        // No refresh token → forced re-login path. Don't silently fabricate one.
        throw new Error('refresh: no refresh token in store')
      }
      const promise = (async (): Promise<string> => {
        const resp = await authApi.refresh(this.refreshToken!)
        // 後端 ROTATE：同時更新 access + refresh（舊 refresh 已失效）。
        this.accessToken = resp.accessToken
        this.refreshToken = resp.refreshToken
        localStorage.setItem('accessToken', resp.accessToken)
        localStorage.setItem('refreshToken', resp.refreshToken)
        return resp.accessToken
      })()
      refreshInFlight = promise
      try {
        return await promise
      } catch (err) {
        // Refresh failed → 撤銷 client-side 狀態。**不靜默**。
        // 401 interceptor 會接著做 toast + redirect；這裡只負責清乾淨。
        this.clearAuthData()
        throw err
      } finally {
        // 無論成功/失敗，下一個呼叫可以重新發起 refresh（如果使用者又登入了）。
        refreshInFlight = null
      }
    },

    async fetchCurrentUser() {
      const user = await authApi.me()
      this.user = user
      // Re-validate the active identity against the authoritative server user.
      // Closes the gap where an identity validated at boot against a (possibly
      // tampered) localStorage user is never re-checked once the real user loads.
      this.activeIdentity = this.validateActiveIdentity(this.activeIdentity)
      localStorage.setItem('activeIdentity', this.activeIdentity)
      return user
    },

    // refreshToken 為可選：一般帳密登入必帶（rotation 生效）；LINE 登入在
    // S3 接上 session store 前尚未發 refresh token（僅 access token，無 rotation）。
    setAuthData(data: Omit<LoginResponse, 'refreshToken'> & { refreshToken?: string | null }) {
      this.accessToken = data.accessToken
      this.refreshToken = data.refreshToken ?? null
      this.user = {
        id: data.userId,
        email: data.email,
        name: data.name,
        role: data.role,
        roles: data.roles ?? [data.role],
      }

      this.activeIdentity = this.defaultIdentity()

      localStorage.setItem('accessToken', data.accessToken)
      if (data.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken)
      } else {
        localStorage.removeItem('refreshToken')
      }
      localStorage.setItem('user', JSON.stringify(this.user))
      localStorage.setItem('activeIdentity', this.activeIdentity)
    },

    switchIdentity(identity: string) {
      if (identity !== this.user?.role && !this.switchableIdentities.includes(identity)) return
      this.activeIdentity = identity
      localStorage.setItem('activeIdentity', identity)
    },

    clearAuthData() {
      this.user = null
      this.accessToken = null
      this.refreshToken = null
      this.activeIdentity = null
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      localStorage.removeItem('activeIdentity')
    },

    initFromStorage() {
      const accessToken = localStorage.getItem('accessToken')
      const refreshToken = localStorage.getItem('refreshToken')
      const userStr = localStorage.getItem('user')
      const activeIdentity = localStorage.getItem('activeIdentity')

      if (accessToken && userStr) {
        this.accessToken = accessToken
        this.refreshToken = refreshToken
        try {
          this.user = JSON.parse(userStr)
          if (this.user) {
            this.activeIdentity = this.validateActiveIdentity(activeIdentity)
            localStorage.setItem('activeIdentity', this.activeIdentity)
          } else {
            this.activeIdentity = null
          }
          // 驗證 token 是否有效（access 或 refresh 任一還活就能撐過去）
          this.fetchCurrentUser().catch(() => this.clearAuthData())
        } catch {
          this.clearAuthData()
        }
      }
    },

    defaultIdentity(): string {
      const identities = this.switchableIdentities
      if (identities.includes('customer')) return 'customer'
      if (identities.length > 0) return identities[0]
      return this.user?.role ?? ''
    },

    validateActiveIdentity(identity: string | null): string {
      if (!this.user) return ''
      if (identity && (identity === this.user.role || this.switchableIdentities.includes(identity))) {
        return identity
      }
      return this.defaultIdentity()
    },

    resetIdentityToPrimary() {
      // Admin context: active identity must equal the primary role so no X-Active-Identity is sent.
      this.activeIdentity = this.user?.role ?? null
      if (this.activeIdentity) {
        localStorage.setItem('activeIdentity', this.activeIdentity)
      }
    },
  },
})
